import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { useSignalImport } from '../../../hooks/useSignalImport';
import { Timestamp } from '../../ui/timestamp';
import DatasetSelector from '../shared/DatasetSelector';
import { useQueryClient } from '@tanstack/react-query';
import { datasetKeys } from '../../../hooks/useDatasets';

interface SignalImportProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: (sessionId: string, signalCount: number) => void;
}

const SignalImport: React.FC<SignalImportProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [datasetId, setDatasetId] = useState<string | null>(null);

  const {
    sessionId,
    uploadFile,
    startImport,
    cancelImport,
    importState,
    progress,
    signals,
    error,
    isUploading,
    isProcessing,
    completedBatches,
    totalBatches,
    estimatedTimeRemaining,
    recentImports,
    clearError,
    isConnected
  } = useSignalImport();

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/json',
      'text/plain'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx?|json|txt)$/i)) {
      alert('Please select a valid file (CSV, Excel, JSON, or TXT)');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 50MB');
      return;
    }

    setSelectedFile(file);
    clearError();
  }, [clearError]);

  // Handle drag and drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle upload and start - Auto-start after successful upload
  const handleUploadAndStart = useCallback(async () => {
    console.log('[SIGNAL-IMPORT-DEBUG] handleUploadAndStart called, selectedFile:', selectedFile?.name);
    if (!selectedFile) {
      console.error('[SIGNAL-IMPORT-DEBUG] No file selected');
      return;
    }

    if (!datasetId) {
      alert('Please select a feedback set.');
      return;
    }

    try {
      console.log('[SIGNAL-IMPORT-DEBUG] Starting file upload...');
      // Upload the file
      const uploadedSessionId = await uploadFile(selectedFile, datasetId);
      console.log('[SIGNAL-IMPORT-DEBUG] Upload completed, session ID:', uploadedSessionId);
      
      if (!uploadedSessionId) {
        console.error('[SIGNAL-IMPORT-DEBUG] No session ID returned from upload');
        throw new Error('No session ID returned from upload');
      }
      
      // Wait a moment for the upload to be fully processed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Automatically start the import after successful upload
      console.log('[SIGNAL-IMPORT-DEBUG] File uploaded successfully, starting import for session:', uploadedSessionId);
      await startImport(uploadedSessionId);
      console.log('[SIGNAL-IMPORT-DEBUG] Start import call completed successfully');
      
    } catch (err) {
      console.error('[SIGNAL-IMPORT-DEBUG] Import failed:', err);
      // Make sure error is displayed to user
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred during import';
      console.error('[SIGNAL-IMPORT-DEBUG] Error message for user:', errorMessage);
    }
  }, [selectedFile, datasetId, uploadFile, startImport]);

  // Handle close
  const handleClose = useCallback(() => {
    if (isProcessing) {
      if (window.confirm('Import is in progress. Are you sure you want to close?')) {
        if (sessionId) {
          cancelImport(sessionId);
        }
        onClose();
      }
    } else {
      if (importState === 'complete' && sessionId && signals.length > 0) {
        onImportComplete?.(sessionId, signals.length);
      }
      onClose();
    }
  }, [isProcessing, sessionId, cancelImport, onClose, importState, signals.length, onImportComplete]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setDatasetId(null);
      clearError();
    }
  }, [isOpen, clearError]);

  // Invalidate datasets query when import completes successfully
  useEffect(() => {
    if (importState === 'complete' && signals.length > 0) {
      // Refresh the feedback set table to show updated record counts and averages
      queryClient.invalidateQueries({ queryKey: datasetKeys.all });
    }
  }, [importState, signals.length, queryClient]);

  // Format time remaining
  const formatTimeRemaining = (seconds: number | null) => {
    if (!seconds) return null;

    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Import Records</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload a file to import multiple Records at once
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Need help formatting your CSV?{' '}
              <a
                href="/v3/import-guide"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 font-semibold"
              >
                Read the CSV Bulk Import Guide
              </a>
              .
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Debug Info - Remove in production */}
          <div className="mb-4 p-3 bg-gray-100 rounded text-xs text-gray-600">
            <strong>Debug:</strong> State: {importState} | Session: {sessionId || 'none'} | 
            Uploading: {isUploading ? 'yes' : 'no'} | Processing: {isProcessing ? 'yes' : 'no'} | 
            Progress: {progress}% | WebSocket: {isConnected ? 'connected' : 'disconnected'}
          </div>
          
          {/* Dataset Selection */}
          {!sessionId && (
            <div className="mb-6">
              <DatasetSelector
                value={datasetId}
                onChange={setDatasetId}
                autoSelectDefault={false}
              />
            </div>
          )}

          {/* File Upload Section */}
          {!sessionId && (
            <div className="mb-8">
              <div
                className={`
                  relative border-2 border-dashed rounded-xl p-8 text-center transition-all
                  ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
                  ${selectedFile ? 'bg-green-50 border-green-300' : ''}
                `}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.json,.txt"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                
                {selectedFile ? (
                  <div className="space-y-4">
                    <CheckCircleIconSolid className="w-12 h-12 text-green-500 mx-auto" />
                    <div>
                      <p className="text-lg font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-600">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto" />
                    <div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Choose a file
                      </button>
                      <span className="text-gray-600"> or drag and drop</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      CSV, Excel, JSON, or TXT files up to 50MB
                    </p>
                  </div>
                )}
              </div>

              {/* Upload and Auto-Start Button */}
              {selectedFile && !sessionId && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleUploadAndStart}
                    disabled={isUploading || isProcessing || !datasetId}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isUploading ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        <span>Uploading & Starting Import...</span>
                      </>
                    ) : isProcessing ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        <span>Starting Import...</span>
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="w-5 h-5" />
                        <span>Import Records</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Import Progress Section */}
          {sessionId && (
            <div className="space-y-6">
              {/* Progress Overview */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Import Progress
                  </h3>
                  {importState === 'processing' && (
                    <button
                      onClick={() => cancelImport(sessionId)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Cancel Import
                    </button>
                  )}
                </div>

                {/* Status Badge */}
                <div className="flex items-center space-x-2 mb-4">
                  {importState === 'processing' && (
                    <>
                      <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />
                      <span className="text-blue-600 font-medium">Processing...</span>
                    </>
                  )}
                  {importState === 'complete' && (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                      <span className="text-green-600 font-medium">Import Complete</span>
                    </>
                  )}
                  {importState === 'error' && (
                    <>
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                      <span className="text-red-600 font-medium">Import Failed</span>
                    </>
                  )}
                  {importState === 'cancelled' && (
                    <>
                      <XMarkIcon className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-600 font-medium">Import Cancelled</span>
                    </>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>{Math.round(progress)}% Complete</span>
                    {estimatedTimeRemaining && importState === 'processing' && (
                      <span className="flex items-center">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {formatTimeRemaining(estimatedTimeRemaining)} remaining
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${
                        importState === 'error' ? 'bg-red-500' :
                        importState === 'complete' ? 'bg-green-500' :
                        'bg-blue-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{signals.length}</p>
                    <p className="text-sm text-gray-600">Records Imported</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {completedBatches}/{totalBatches}
                    </p>
                    <p className="text-sm text-gray-600">Batches Processed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {importState === 'processing' ? (
                        <ArrowPathIcon className="w-6 h-6 animate-spin inline" />
                      ) : (
                        signals.length > 0 ? '100%' : '0%'
                      )}
                    </p>
                    <p className="text-sm text-gray-600">Success Rate</p>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                    <div>
                      <p className="text-red-800 font-medium">Import Error</p>
                      <p className="text-red-700 text-sm mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Records */}
              {signals.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Recent Imported Records
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    <AnimatePresence mode="popLayout">
                      {signals.slice(-5).reverse().map((signal, index) => (
                        <motion.div
                          key={signal.signal_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <SparklesIcon className="w-4 h-4 text-yellow-500" />
                                <span className="text-sm font-medium text-gray-900">
                                  {signal.customer_name || 'Unknown Customer'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  • {signal.channel_type || 'Unknown Channel'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {signal.raw_text || signal.verbatim || 'No content available'}
                              </p>
                              {signal.classification && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {signal.classification.labels?.slice(0, 3).map((label: any) => (
                                    <span
                                      key={label.id}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {label.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <CheckCircleIconSolid className="w-5 h-5 text-green-500 ml-2" />
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recent Import History */}
          {!sessionId && recentImports.length > 0 && (
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Recent Imports
              </h3>
              <div className="space-y-2">
                {recentImports.slice(0, 3).map((importItem) => (
                  <div
                    key={importItem.sessionId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <DocumentDuplicateIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {importItem.fileName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {importItem.signalCount} signals • <Timestamp value={importItem.timestamp} format="relative" />
                        </p>
                      </div>
                    </div>
                    {importItem.status === 'complete' && (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {sessionId && (
              <span>Session ID: {sessionId}</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {importState === 'complete' && signals.length > 0 && (
              <button
                onClick={() => {
                  onImportComplete?.(sessionId!, signals.length);
                  onClose();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                View Imported Records
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {importState === 'complete' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SignalImport;